(() => {
  'use strict';

  const EVENT_NAMES = {
    viewed: 'eversnug_creator_application_viewed',
    started: 'eversnug_creator_application_started',
    stepCompleted: 'eversnug_creator_application_step_completed',
    submitted: 'eversnug_creator_application_submitted',
    onboardingClicked: 'eversnug_creator_tiktok_onboarding_clicked'
  };
  const KLAVIYO_EVENT_REVISION = '2026-04-15';
  const KLAVIYO_SUBSCRIPTION_REVISION = '2026-07-15';
  const FORM_VERSION = 'creator-v1';
  const MAX_ATTRIBUTION_LENGTH = 120;
  const MINIMUM_DWELL_MS = 1200;
  const REQUEST_TIMEOUT_MS = 8000;

  const suppressConsumerPopups = () => {
    if (document.body?.dataset.templateSuffix !== 'creators') return;

    if (document.body.classList.contains('has-onsite-popup') || document.body.classList.contains('klaviyo-prevent-body-scrolling')) {
      document.body.classList.remove('has-onsite-popup', 'klaviyo-prevent-body-scrolling');
    }
    if (document.documentElement.classList.contains('klaviyo-prevent-body-scrolling')) {
      document.documentElement.classList.remove('klaviyo-prevent-body-scrolling');
    }
    if (document.body.style.overflow) document.body.style.removeProperty('overflow');
    if (document.documentElement.style.overflow) document.documentElement.style.removeProperty('overflow');

    document.querySelectorAll('[data-kl-scroll-locking-modal="true"], [aria-modal="true"][aria-label="POPUP Form"], [data-testid="animated-teaser"], iframe[src*="richpanel"], [id^="richpanel"]')
      .forEach((popup) => {
        const wrapper = popup.closest('[style*="position: fixed"]') || popup;
        if (!wrapper.hidden) wrapper.hidden = true;
        if (wrapper.getAttribute('aria-hidden') !== 'true') wrapper.setAttribute('aria-hidden', 'true');
        if (wrapper.style.getPropertyValue('display') !== 'none' || wrapper.style.getPropertyPriority('display') !== 'important') {
          wrapper.style.setProperty('display', 'none', 'important');
        }
      });
  };

  const createApplicationId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `creator-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const clean = (value, maximum = 300) => String(value || '').trim().slice(0, maximum);

  const normalizeTikTok = (value) => {
    const normalized = clean(value, 160);
    if (!normalized) return '';
    if (/^https?:\/\//i.test(normalized)) return normalized;
    if (/^(www\.)?tiktok\.com\//i.test(normalized)) return `https://${normalized}`;
    if (normalized.startsWith('@')) return normalized;
    if (!normalized.includes(' ') && !normalized.includes('/')) return `@${normalized.replace(/^@/, '')}`;
    return normalized;
  };

  const collectAttribution = () => {
    const query = new URLSearchParams(window.location.search);
    const allowed = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    return allowed.reduce((result, key) => {
      const value = clean(query.get(key), MAX_ATTRIBUTION_LENGTH);
      if (value) result[key] = value;
      return result;
    }, {});
  };

  const publishShopifyEvent = (root, eventName, payload = {}) => {
    if (root.dataset.shopifyDesignMode === 'true') return;

    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(eventName, payload);
      }
    } catch (_error) {
      // Analytics must never interrupt the application.
    }
  };

  const postWithRetry = async (url, options, acceptedStatuses, retryStatuses = [429, 503]) => {
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        window.clearTimeout(timeout);

        if (acceptedStatuses.includes(response.status)) return response;

        const canRetry = attempt === 0 && retryStatuses.includes(response.status);
        if (!canRetry) {
          const error = new Error('Application service rejected the request.');
          error.status = response.status;
          throw error;
        }

        const retryAfter = Number(response.headers.get('Retry-After'));
        await delay(Number.isFinite(retryAfter) ? Math.min(retryAfter * 1000, 2500) : 900);
      } catch (error) {
        window.clearTimeout(timeout);
        lastError = error;

        if (attempt > 0 || (error.status && !retryStatuses.includes(error.status))) {
          throw error;
        }

        await delay(900);
      }
    }

    throw lastError || new Error('Application service unavailable.');
  };

  const buildSubmission = (form, applicationId, attribution) => {
    const selectedRoute = form.querySelector('input[name="contact[Creator route]"]:checked') ||
      form.querySelector('input[name="contact[Creator route]"]');
    const categories = Array.from(form.querySelectorAll('input[name="contact[Content categories][]"]:checked'))
      .map((input) => clean(input.value, 60));

    return {
      applicationId,
      submittedAt: new Date().toISOString(),
      formVersion: FORM_VERSION,
      route: clean(selectedRoute && selectedRoute.value, 80),
      email: clean(form.querySelector('input[name="contact[email]"]')?.value, 100).toLowerCase(),
      tiktokProfile: normalizeTikTok(form.querySelector('input[name="contact[TikTok profile]"]')?.value),
      bestContentUrl: clean(form.querySelector('input[name="contact[Best content URL]"]')?.value, 300),
      tiktokShopStatus: clean(form.querySelector('[name="contact[TikTok Shop status]"]:checked')?.value, 80),
      contentCategories: categories,
      videoIdea: clean(form.querySelector('textarea[name="contact[Video idea]"]')?.value, 200),
      eligibilityConfirmed: Boolean(form.querySelector('input[name="contact[Eligibility attestation]"]')?.checked),
      // Consent is disclosed as agreement-by-submission next to the submit button.
      contentCommitmentConfirmed: true,
      applicationConsentConfirmed: true,
      marketingOptIn: Boolean(form.querySelector('[data-marketing-opt-in]')?.checked),
      attribution,
      pagePath: clean(window.location.pathname, 180)
    };
  };

  const KLAVIYO_METRICS = {
    // Fired the moment step 1 is sent, so a step-2 abandoner is a recoverable
    // lead instead of a discarded email address.
    started: 'Creator Application Started',
    // Unchanged: still means a completed application (Meta Lead semantics).
    submitted: 'Creator Application Submitted'
  };

  const buildKlaviyoEvent = (submission, metricName) => ({
    data: {
      type: 'event',
      attributes: {
        properties: {
          'Application ID': submission.applicationId,
          'Form Version': submission.formVersion,
          'Creator Route': submission.route,
          'TikTok Profile': submission.tiktokProfile,
          'Best Content URL': submission.bestContentUrl,
          'TikTok Shop Status': submission.tiktokShopStatus,
          'Content Categories': submission.contentCategories,
          'Video Idea': submission.videoIdea,
          'Eligibility Confirmed': submission.eligibilityConfirmed,
          'Content Commitment Confirmed': submission.contentCommitmentConfirmed,
          'Application Consent Confirmed': submission.applicationConsentConfirmed,
          'Marketing Opt In': submission.marketingOptIn,
          'UTM Source': submission.attribution.utm_source || '',
          'UTM Medium': submission.attribution.utm_medium || '',
          'UTM Campaign': submission.attribution.utm_campaign || '',
          'UTM Content': submission.attribution.utm_content || '',
          'UTM Term': submission.attribution.utm_term || '',
          'Page Path': submission.pagePath
        },
        metric: {
          data: {
            type: 'metric',
            attributes: { name: metricName || KLAVIYO_METRICS.submitted }
          }
        },
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: submission.email,
              properties: {
                'EverSnug Creator Route': submission.route,
                'EverSnug TikTok Profile': submission.tiktokProfile,
                'EverSnug TikTok Shop Status': submission.tiktokShopStatus,
                'EverSnug Creator Application ID': submission.applicationId
              }
            }
          }
        },
        // One unique_id per metric, so the started and submitted events for the
        // same application are not deduplicated against each other.
        unique_id: metricName === KLAVIYO_METRICS.started
          ? `${submission.applicationId}-started`
          : submission.applicationId,
        time: submission.submittedAt
      }
    }
  });

  const submitKlaviyoEvent = (siteId, submission, metricName) => {
    if (!siteId) throw new Error('Klaviyo Site ID is not configured.');

    const url = `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(siteId)}`;
    return postWithRetry(url, {
      method: 'POST',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_EVENT_REVISION
      },
      body: JSON.stringify(buildKlaviyoEvent(submission, metricName))
    }, [202]);
  };

  const subscribeToCreatorMarketing = (siteId, listId, submission) => {
    if (!submission.marketingOptIn || !siteId || !listId) return Promise.resolve();

    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(siteId)}`;
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'EverSnug Creator Program',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: submission.email,
                subscriptions: {
                  email: {
                    marketing: { consent: 'SUBSCRIBED' }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };

    return postWithRetry(url, {
      method: 'POST',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_SUBSCRIPTION_REVISION
      },
      body: JSON.stringify(body)
    }, [202]);
  };

  const submitToServer = (endpoint, submission) => {
    if (!endpoint) throw new Error('Server endpoint is not configured.');

    return postWithRetry(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'omit',
      body: JSON.stringify(submission)
    }, [200, 201, 202, 204]);
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveDataEnabled = () => Boolean(navigator.connection && navigator.connection.saveData);
  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

  const createMediaPlayer = (wrapper) => {
    const video = wrapper.querySelector('video');
    if (!video) return null;

    const control = wrapper.querySelector('[data-media-control]');
    const label = wrapper.querySelector('[data-media-control-label]');
    let loaded = false;
    let manuallyPaused = false;

    const updateControl = () => {
      if (!control) return;
      const playing = !video.paused;
      control.setAttribute('aria-pressed', String(playing));
      control.setAttribute('aria-label', playing ? 'Pause creator video' : 'Play creator video');
      if (label) label.textContent = playing ? 'Pause' : 'Play';
    };

    const load = () => {
      if (loaded) return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      video.load();
      loaded = true;
    };

    const unload = () => {
      if (!loaded) return;
      if (!video.paused) video.pause();
      video.querySelectorAll('source[src]').forEach((source) => {
        source.removeAttribute('src');
      });
      video.load();
      loaded = false;
      updateControl();
    };

    const play = () => {
      if (manuallyPaused || prefersReducedMotion() || saveDataEnabled()) return;
      load();
      video.play().then(updateControl).catch(updateControl);
    };

    const pause = () => {
      if (!video.paused) video.pause();
      updateControl();
    };

    if (control) {
      control.addEventListener('click', () => {
        load();
        if (video.paused) {
          manuallyPaused = false;
          video.play().then(updateControl).catch(updateControl);
        } else {
          manuallyPaused = true;
          video.pause();
          updateControl();
        }
      });
    }

    video.addEventListener('play', updateControl);
    video.addEventListener('pause', updateControl);
    updateControl();

    return { wrapper, video, load, unload, play, pause };
  };

  const initializeRail = (root) => {
    const rail = root.querySelector('[data-phone-rail]');
    if (!rail) return;

    const players = Array.from(rail.querySelectorAll('[data-deferred-media]'))
      .map(createMediaPlayer)
      .filter(Boolean);
    if (!players.length) return;

    const nav = root.querySelector('[data-rail-nav]');
    const dotsWrap = root.querySelector('[data-rail-dots]');
    let dots = [];
    if (dotsWrap) {
      dots = players.map((_, index) => {
        const dot = document.createElement('i');
        if (index === 0) dot.className = 'is-active';
        dotsWrap.appendChild(dot);
        return dot;
      });
    }

    let railOnScreen = false;

    const cardStep = () => {
      if (players.length < 2) return rail.clientWidth || 1;
      return Math.max(1, players[1].wrapper.offsetLeft - players[0].wrapper.offsetLeft);
    };

    const syncNav = () => {
      const index = Math.min(players.length - 1, Math.max(0, Math.round(rail.scrollLeft / cardStep())));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    // Only the mostly-visible cards play; peeking neighbours load quietly;
    // anything further than one card outside the viewport is torn down.
    const syncMedia = () => {
      const start = rail.scrollLeft;
      const end = start + rail.clientWidth;
      const base = players[0].wrapper.offsetLeft;

      let playing = 0;
      players.forEach((player) => {
        const left = player.wrapper.offsetLeft - base;
        const width = player.wrapper.offsetWidth || 1;

        if (!railOnScreen) {
          player.pause();
          return;
        }

        const visible = Math.min(end, left + width) - Math.max(start, left);
        const ratio = visible / width;

        if (ratio >= 0.6 && playing < 2) {
          playing += 1;
          player.play();
        } else if (ratio > 0.05) {
          player.pause();
          player.load();
        } else {
          player.pause();
          const nearby = left + width > start - width && left < end + width;
          if (!nearby) player.unload();
        }
      });
    };

    const showNavIfScrollable = () => {
      if (!nav) return;
      nav.hidden = rail.scrollWidth <= rail.clientWidth + 4;
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        syncNav();
        syncMedia();
      });
    };

    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      showNavIfScrollable();
      onScroll();
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        railOnScreen = entries.some((entry) => entry.isIntersecting);
        syncMedia();
      }, { rootMargin: '80px' });
      observer.observe(rail);
    } else {
      railOnScreen = true;
      syncMedia();
    }

    const advance = root.querySelector('[data-rail-advance]');
    if (advance) {
      advance.addEventListener('click', () => {
        const step = cardStep();
        const current = Math.round(rail.scrollLeft / step);
        const next = current + 1 >= players.length ? 0 : current + 1;
        rail.scrollTo({ left: next * step, behavior: scrollBehavior() });
      });
    }

    showNavIfScrollable();
    syncNav();
  };

  const initializeDeferredMedia = (root) => {
    root.querySelectorAll('[data-deferred-media]').forEach((wrapper) => {
      if (wrapper.closest('[data-phone-rail]')) return;

      const player = createMediaPlayer(wrapper);
      if (!player) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) player.play();
            else player.pause();
          });
        }, { rootMargin: '80px' });
        observer.observe(wrapper);
      } else {
        player.play();
      }
    });

    initializeRail(root);
  };

  const initializeMarqueePause = (root) => {
    root.querySelectorAll('.cr-marquee__track').forEach((track) => {
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        track.classList.toggle('is-paused');
      });
    });
  };

  const initializeCreatorProgram = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.classList.add('creator-program--enhanced');

    const form = root.querySelector('.creator-application-form');
    const applicationCard = root.querySelector('[data-application-card]');
    const success = root.querySelector('[data-application-success]');
    const stickyCta = root.querySelector('[data-sticky-cta]');
    const attribution = collectAttribution();
    let applicationId = createApplicationId();
    const openedAt = Date.now();
    let currentStep = 1;
    let started = false;
    let startedEventSent = false;
    let startInFlight = false;
    let submissionInFlight = false;
    let nativeSubmission = false;
    let formVisible = false;
    let formFocused = false;
    let finalCtaVisible = false;
    let offerCtaVisible = false;
    let heroInView = true;
    let keyboardOpen = false;
    let completed = false;

    if (!form || !success) return;

    const applicationIdField = form.querySelector('[data-application-id]');
    if (applicationIdField) applicationIdField.value = applicationId;

    Object.entries(attribution).forEach(([key, value]) => {
      const input = form.querySelector(`[data-attribution-field="${key}"]`);
      if (input) input.value = value;
    });

    const updateStickyCta = () => {
      if (!stickyCta) return;
      stickyCta.classList.toggle('is-hidden', completed || formVisible || formFocused || finalCtaVisible || offerCtaVisible || heroInView || keyboardOpen);
    };

    // iOS strands fixed elements mid-screen above the keyboard; hide the bar
    // whenever the visual viewport shrinks meaningfully.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        keyboardOpen = window.visualViewport.height < window.innerHeight * 0.8;
        updateStickyCta();
      });
    }

    const setStep = (step, options = {}) => {
      const { focus = true, scroll = true } = options;
      currentStep = step;
      root.querySelectorAll('[data-form-step]').forEach((fieldset) => {
        fieldset.hidden = Number(fieldset.dataset.formStep) !== step;
      });
      root.querySelectorAll('[data-progress-step]').forEach((progressStep) => {
        progressStep.classList.toggle('is-active', Number(progressStep.dataset.progressStep) === step);
      });

      // Step 1 reads as a single clean ask; progress context appears on step 2.
      const progressBar = root.querySelector('[data-progress]');
      if (progressBar) progressBar.hidden = step === 1;
      const cardTitle = root.querySelector('[data-form-card-title]');
      if (cardTitle) cardTitle.textContent = step === 2 ? 'Almost there' : 'Apply now';

      if (scroll) {
        applicationCard.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      }
      if (focus) {
        // Focus the fieldset itself (tabindex="-1") so mobile keyboards
        // don't pop open mid-scroll and focus never lands on an invisible input.
        const fieldset = form.querySelector(`[data-form-step="${step}"]`);
        if (fieldset) fieldset.focus({ preventScroll: true });
      }
    };

    const showStepError = (step, message) => {
      const error = form.querySelector(`[data-step-error="${step}"]`);
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    };

    const fieldErrors = new Map();
    let fieldErrorId = 0;

    const clearFieldError = (field) => {
      const note = fieldErrors.get(field);
      field.removeAttribute('aria-invalid');
      if (!note) return;
      if (field.getAttribute('aria-describedby') === note.id) {
        field.removeAttribute('aria-describedby');
      }
      note.remove();
      fieldErrors.delete(field);
    };

    const showFieldError = (field, message) => {
      clearFieldError(field);
      fieldErrorId += 1;
      const note = document.createElement('span');
      note.className = 'creator-field-error';
      note.id = `CreatorFieldError-${fieldErrorId}`;
      note.textContent = message;

      const host = field.closest('.creator-field') || field.closest('.creator-check');
      if (host && host.classList.contains('creator-check')) {
        host.insertAdjacentElement('afterend', note);
      } else if (host) {
        host.appendChild(note);
      } else {
        field.insertAdjacentElement('afterend', note);
      }

      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', note.id);
      fieldErrors.set(field, note);
    };

    const messageFor = (field) => {
      if (field.type === 'radio') return 'Please choose one.';
      if (field.type === 'checkbox') return 'Please tick this box to continue.';
      if (field.validity && field.validity.valueMissing) {
        return field.tagName === 'SELECT' ? 'Please choose an option.' : 'This field is required.';
      }
      if (field.type === 'email') return 'Enter a valid email, like you@email.com.';
      return 'Please check this entry.';
    };

    const validateStep = (step) => {
      const fieldset = form.querySelector(`[data-form-step="${step}"]`);
      if (!fieldset) return false;

      showStepError(step, '');
      fieldset.querySelectorAll('input, select, textarea').forEach(clearFieldError);

      const seenRadioGroups = new Set();
      const invalidFields = Array.from(fieldset.querySelectorAll('input, select, textarea'))
        .filter((field) => !field.checkValidity())
        .filter((field) => {
          if (field.type !== 'radio') return true;
          if (seenRadioGroups.has(field.name)) return false;
          seenRadioGroups.add(field.name);
          return true;
        });
      invalidFields.forEach((field) => showFieldError(field, messageFor(field)));

      if (invalidFields.length) {
        const first = invalidFields[0];
        first.focus({ preventScroll: true });
        const anchor = first.closest('.creator-field') || first.closest('.creator-check') || first;
        anchor.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
        return false;
      }

      return true;
    };

    let lastCtaSource = 'direct';
    root.querySelectorAll('[data-cta-source]').forEach((cta) => {
      cta.addEventListener('click', () => { lastCtaSource = cta.getAttribute('data-cta-source') || 'direct'; });
    });

    const markStarted = () => {
      if (started) return;
      started = true;
      const route = form.querySelector('input[name="contact[Creator route]"]:checked')?.value ||
        form.querySelector('input[name="contact[Creator route]"]')?.value || '';
      publishShopifyEvent(root, EVENT_NAMES.started, {
        application_id: applicationId,
        form_version: FORM_VERSION,
        route: clean(route, 80),
        cta_source: lastCtaSource
      });
    };

    const showSuccess = () => {
      completed = true;
      submissionInFlight = false;
      form.hidden = true;
      const progress = root.querySelector('[data-progress]');
      if (progress) progress.hidden = true;
      const header = applicationCard.querySelector('.creator-form-card__header');
      if (header) header.hidden = true;
      const modeBanner = applicationCard.querySelector('.creator-mode-banner');
      if (modeBanner) modeBanner.hidden = true;
      success.hidden = false;
      updateStickyCta();
      success.focus({ preventScroll: true });
      applicationCard.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    };

    const setSubmitting = (isSubmitting) => {
      submissionInFlight = isSubmitting;
      const button = form.querySelector('[data-submit-application]');
      const label = form.querySelector('[data-submit-label]');
      if (!button) return;
      button.disabled = isSubmitting;
      button.classList.toggle('is-submitting', isSubmitting);
      if (label) label.textContent = isSubmitting ? 'Sending…' : 'Finish application';
    };

    const setStarting = (isStarting) => {
      startInFlight = isStarting;
      const button = form.querySelector('[data-next-step]');
      const label = form.querySelector('[data-start-label]');
      if (!button) return;
      button.disabled = isStarting;
      button.classList.toggle('is-submitting', isStarting);
      if (label) label.textContent = isStarting ? 'Sending…' : 'Send application';
    };

    // Capture-first: step 1 sends what we already have before asking for more.
    // A failure here must never block the applicant, so the advance to step 2
    // happens either way and the completed submission remains the source of truth.
    const sendStartedEvent = async () => {
      if (startedEventSent) return;
      startedEventSent = true;

      try {
        const submission = buildSubmission(form, applicationId, attribution);
        if (root.dataset.submissionMode === 'klaviyo') {
          await submitKlaviyoEvent(root.dataset.klaviyoSiteId, submission, KLAVIYO_METRICS.started);
        } else if (root.dataset.submissionMode === 'server') {
          await submitToServer(root.dataset.serverEndpoint, { ...submission, stage: 'started' });
        }
      } catch (_error) {
        // Recoverable-lead capture is best effort; the applicant continues.
        startedEventSent = false;
      }
    };

    setStep(1, { focus: false, scroll: false });
    initializeDeferredMedia(root);
    initializeMarqueePause(root);
    publishShopifyEvent(root, EVENT_NAMES.viewed, {
      application_id: applicationId,
      form_version: FORM_VERSION,
      page_path: clean(window.location.pathname, 180),
      ...attribution
    });

    if ('IntersectionObserver' in window) {
      const seenSections = new Set();
      const depthTargets = [
        ['hero', '.cr-hero'], ['videos', '.cr-content'], ['offer', '.cr-offer'],
        ['apply', '.cr-apply'], ['marquee', '.cr-marquee'], ['faq', '.cr-faq'],
        ['final', '.cr-final']
      ];
      const depthObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const key = entry.target.getAttribute('data-depth-key');
          if (!key || seenSections.has(key)) return;
          seenSections.add(key);
          publishShopifyEvent(root, 'eversnug_creator_section_viewed', {
            application_id: applicationId,
            form_version: FORM_VERSION,
            section: key
          });
        });
      }, { threshold: 0.25 });
      depthTargets.forEach(([key, sel]) => {
        const el = root.querySelector(sel);
        if (el) { el.setAttribute('data-depth-key', key); depthObserver.observe(el); }
      });
    }

    form.addEventListener('input', markStarted, { once: true });
    form.addEventListener('change', markStarted, { once: true });

    form.addEventListener('input', (event) => {
      if (event.target.matches('[aria-invalid="true"]')) clearFieldError(event.target);
    });
    form.addEventListener('change', (event) => {
      if (event.target.matches('[aria-invalid="true"]')) clearFieldError(event.target);
      const pillGroup = event.target.closest('[data-shop-status-group]');
      if (pillGroup) {
        pillGroup.querySelectorAll('input').forEach(clearFieldError);
      }
    });

    form.querySelector('[data-form-step="1"]')?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;
      if (event.target.type === 'checkbox') return;
      event.preventDefault();
      form.querySelector('[data-next-step]')?.click();
    });

    form.querySelector('[data-next-step]')?.addEventListener('click', async () => {
      if (startInFlight || submissionInFlight) return;
      if (!validateStep(1)) return;

      if (form.querySelector('[data-honeypot]')?.value) {
        showSuccess();
        return;
      }

      const route = form.querySelector('input[name="contact[Creator route]"]:checked')?.value ||
        form.querySelector('input[name="contact[Creator route]"]')?.value || '';

      setStarting(true);
      await sendStartedEvent();
      setStarting(false);

      publishShopifyEvent(root, EVENT_NAMES.stepCompleted, {
        application_id: applicationId,
        form_version: FORM_VERSION,
        step: 1,
        route: clean(route, 80),
        cta_source: lastCtaSource
      });
      setStep(2);
    });

    form.querySelector('[data-previous-step]')?.addEventListener('click', () => setStep(1));

    const idea = form.querySelector('[data-idea-field]');
    const characterCount = form.querySelector('[data-character-count]');
    if (idea && characterCount) {
      idea.addEventListener('input', () => {
        characterCount.textContent = String(idea.value.length);
      });
    }

    form.addEventListener('focusin', () => {
      formFocused = true;
      updateStickyCta();
    });
    form.addEventListener('focusout', () => {
      window.setTimeout(() => {
        formFocused = form.contains(document.activeElement);
        updateStickyCta();
      }, 0);
    });

    if ('IntersectionObserver' in window && applicationCard) {
      const observer = new IntersectionObserver((entries) => {
        formVisible = entries.some((entry) => entry.isIntersecting);
        updateStickyCta();
      }, { threshold: 0.08 });
      observer.observe(applicationCard);
    }

    if ('IntersectionObserver' in window) {
      const finalSection = root.querySelector('.cr-final');
      if (finalSection) {
        const finalObserver = new IntersectionObserver((entries) => {
          finalCtaVisible = entries.some((entry) => entry.isIntersecting);
          updateStickyCta();
        }, { threshold: 0.2 });
        finalObserver.observe(finalSection);
      }

      // Two "Apply now" pills must never share the screen: the offer CTA
      // stands the bar down for as long as it is on screen.
      const offerCta = root.querySelector('.cr-offer__cta');
      if (offerCta) {
        const offerObserver = new IntersectionObserver((entries) => {
          offerCtaVisible = entries.some((entry) => entry.isIntersecting);
          updateStickyCta();
        }, { threshold: 0.4 });
        offerObserver.observe(offerCta);
      }

      // The bar earns its place only once the hero's own CTA has scrolled away.
      const heroCta = root.querySelector('.cr-hero__actions') || root.querySelector('.cr-hero');
      if (heroCta) {
        const heroObserver = new IntersectionObserver((entries) => {
          heroInView = entries.some((entry) => entry.isIntersecting);
          updateStickyCta();
        });
        heroObserver.observe(heroCta);
      } else {
        heroInView = false;
      }
    } else {
      heroInView = false;
    }

    form.addEventListener('submit', async (event) => {
      if (nativeSubmission) return;
      event.preventDefault();
      if (submissionInFlight || !validateStep(2)) return;

      if (form.querySelector('[data-honeypot]')?.value) {
        showSuccess();
        return;
      }

      setSubmitting(true);
      showStepError(2, '');
      const fallback = form.querySelector('[data-native-fallback]');
      if (fallback) fallback.hidden = true;

      try {
        const remainingDwell = MINIMUM_DWELL_MS - (Date.now() - openedAt);
        if (remainingDwell > 0) await delay(remainingDwell);

        const submission = buildSubmission(form, applicationId, attribution);
        const mode = root.dataset.submissionMode;

        if (mode === 'klaviyo') {
          await submitKlaviyoEvent(root.dataset.klaviyoSiteId, submission, KLAVIYO_METRICS.submitted);
          subscribeToCreatorMarketing(
            root.dataset.klaviyoSiteId,
            root.dataset.klaviyoListId,
            submission
          ).catch(() => {});
        } else if (mode === 'server') {
          await submitToServer(root.dataset.serverEndpoint, submission);
        } else {
          throw new Error('Submission mode is not configured.');
        }

        publishShopifyEvent(root, EVENT_NAMES.submitted, {
          application_id: applicationId,
          form_version: FORM_VERSION,
          route: submission.route,
          tiktok_shop_status: submission.tiktokShopStatus,
          marketing_opt_in: submission.marketingOptIn,
          ...attribution
        });
        showSuccess();
      } catch (_error) {
        setSubmitting(false);
        showStepError(2, 'We could not send your application right now. Check your connection or use the secure backup form below.');
        if (fallback) fallback.hidden = false;
      }
    });

    form.querySelector('[data-submit-native]')?.addEventListener('click', () => {
      nativeSubmission = true;
      try {
        window.sessionStorage.setItem('eversnug-creator-app-id', applicationId);
      } catch (_error) { /* storage unavailable */ }
      form.submit();
    });

    root.querySelector('[data-tiktok-onboarding]')?.addEventListener('click', () => {
      publishShopifyEvent(root, EVENT_NAMES.onboardingClicked, {
        application_id: applicationId,
        form_version: FORM_VERSION
      });
    });

    if (form.querySelector('.creator-native-message--success')) {
      try {
        const storedId = window.sessionStorage.getItem('eversnug-creator-app-id');
        if (storedId) applicationId = storedId;
        window.sessionStorage.removeItem('eversnug-creator-app-id');
      } catch (_error) { /* storage unavailable */ }
      showSuccess();
    } else {
      updateStickyCta();
    }
  };

  const initializeAll = (scope = document) => {
    suppressConsumerPopups();
    scope.querySelectorAll('[data-creator-program]').forEach(initializeCreatorProgram);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  if (document.body?.dataset.templateSuffix === 'creators') {
    const popupObserver = new MutationObserver(suppressConsumerPopups);
    popupObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }
})();
